import { blocks, type Block } from '@w/content-blocks'
import { crdt, type Doc } from '@w/crdt'
import { pageDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import type { PageDocumentJson } from './page-document'

export const maximumPageContentBytes = 5_000_000

/** Canonical body serialization excludes frontmatter, binary media and CRDT history. */
export function assertPageContentSize(pageId: string, document: Pick<PageDocumentJson, 'content'>, maximum = maximumPageContentBytes) {
  const content = document.content
  const body = 'children' in content
    ? blocks.serialize(pageDocument({ toJSON: () => document as unknown as ReturnType<Doc['toJSON']> }).content.children as unknown as Block[])
    : JSON.stringify(content)
  const bytes = new TextEncoder().encode(body).byteLength
  if (bytes > maximum) throw new Error(`Page ${pageId} contains ${bytes} bytes; maximum page content is ${maximum} bytes. Content was not saved.`)
}

/** Markdown body bytes per merged-snapshot byte, at most: measured worst 4.61 (text of `&`, serialized `&amp;`), others
 * ≤ 2.6 (escapes in questions, quotes, tables) and ≤ 0.61 on 1,000 bench pages; ×3.5 margin. Pinned by page-content-size.node.test.ts. */
export const markdownBytesPerSnapshotByte = 16

/** The limit as the page is written: the merged snapshot's length bounds its body, so only a page within reach of the
 * limit is decoded and serialized (workspace-pages.spec.md#page-size). */
export function assertMergedPageContentSize(pageId: string, snapshot: Uint8Array, maximum = maximumPageContentBytes) {
  if (snapshot.byteLength * markdownBytesPerSnapshotByte <= maximum) return
  assertPageContentSize(pageId, crdt.load(snapshot).toJSON() as unknown as Pick<PageDocumentJson, 'content'>, maximum)
}
